import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

export const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND}/api`;

// Prefix locally-uploaded images (/api/uploads/..) with the backend origin.
export const mediaUrl = (u) => (!u ? u : String(u).startsWith("/api/") ? `${BACKEND}${u}` : u);

const ContentContext = createContext(null);

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState(null);
  const [error, setError] = useState(false);

  const reload = useCallback(async () => {
    setError(false);
    const isPreview = new URLSearchParams(window.location.search).get("preview") === "1";
    const url = isPreview ? `${API}/content?preview=1` : `${API}/content`;
    const { data } = await axios.get(url, { withCredentials: isPreview });
    setContent(data);
    return data;
  }, []);

  useEffect(() => { reload().catch(() => setError(true)); }, [reload]);

  useEffect(() => {
    if (!content) return;
    // Per-page SEO (falls back to global seo).
    const path = typeof window !== "undefined" ? window.location.pathname : "/";
    const page = (content.seoPages || {})[path] || {};
    const title = page.title || content.seo?.title;
    const desc = page.description || content.seo?.description;
    if (title) document.title = title;
    const m = document.querySelector('meta[name="description"]');
    if (m && desc) m.setAttribute("content", desc);
    // Social sharing meta (best-effort for JS-rendering crawlers).
    const setMeta = (sel, attr, val) => { const el = document.querySelector(sel); if (el && val) el.setAttribute(attr, val); };
    const ogImage = page.ogImage || content.seo?.ogImage;
    if (title) { setMeta('meta[property="og:title"]', "content", title); setMeta('meta[name="twitter:title"]', "content", title); }
    if (desc) { setMeta('meta[property="og:description"]', "content", desc); setMeta('meta[name="twitter:description"]', "content", desc); }
    if (ogImage) { setMeta('meta[property="og:image"]', "content", ogImage); setMeta('meta[name="twitter:image"]', "content", ogImage); }

    // Runtime theming: colors (channel format for opacity utilities) + fonts.
    const hexToChannels = (hex) => {
      if (!hex || typeof hex !== "string") return null;
      const h = hex.replace("#", "");
      if (h.length !== 6) return null;
      const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
      return [r, g, b].some(isNaN) ? null : `${r} ${g} ${b}`;
    };
    const root = document.documentElement;
    const cols = content.theme?.colors || {};
    const map = { amber: "--c-amber", ember: "--c-ember", gold: "--c-gold", deepred: "--c-deepred" };
    Object.entries(map).forEach(([k, cssVar]) => {
      const ch = hexToChannels(cols[k]);
      if (ch) root.style.setProperty(cssVar, ch);
    });
    const fonts = content.theme?.fonts || {};
    if (fonts.display) root.style.setProperty("--f-display", `"${fonts.display}"`);
    if (fonts.body) root.style.setProperty("--f-body", `"${fonts.body}"`);
  }, [content]);

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#050505] px-6 text-center">
        <div>
          <p className="font-display text-3xl text-white">Unable to load content</p>
          <p className="mt-3 text-white/50 text-sm">Please check your connection and try again.</p>
          <button onClick={() => reload().catch(() => setError(true))} className="mt-6 rounded-full bg-amberwarm px-6 py-2.5 text-sm font-semibold text-black">Retry</button>
        </div>
      </div>
    );
  }
  if (content === null) {
    return <div className="min-h-screen grid place-items-center bg-[#050505] text-white/40">Loading…</div>;
  }
  const isPreview = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("preview") === "1";
  return (
    <ContentContext.Provider value={{ content, reload }}>
      {isPreview && (
        <div data-testid="preview-banner" className="fixed bottom-0 inset-x-0 z-[100] bg-amberwarm text-black text-center text-xs font-semibold uppercase tracking-[0.2em] py-2 shadow-[0_-4px_20px_rgba(0,0,0,0.4)]">
          Draft Preview — not published · changes are only visible to you
        </div>
      )}
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = () => useContext(ContentContext).content;
export const useContentCtx = () => useContext(ContentContext);
